import type { ReactNode } from "react";
import clsx from "clsx";

export function StatCard({
  label,
  value,
  hint,
  icon,
  tone = "default",
}: {
  label: string;
  value: string | number;
  hint?: string;
  icon?: ReactNode;
  tone?: "default" | "green" | "yellow" | "red" | "blue";
}): JSX.Element {
  const toneClass: Record<string, { text: string; iconBg: string; accent: string }> = {
    default: {
      text: "text-slate-900 dark:text-slate-100",
      iconBg: "bg-brand-50 text-brand-600 dark:bg-brand-900/40 dark:text-brand-400",
      accent: "from-brand-400 to-brand-600",
    },
    green: {
      text: "text-green-700 dark:text-green-400",
      iconBg: "bg-green-50 text-green-600 dark:bg-green-900/40 dark:text-green-400",
      accent: "from-green-400 to-green-600",
    },
    yellow: {
      text: "text-yellow-700 dark:text-yellow-400",
      iconBg: "bg-yellow-50 text-yellow-600 dark:bg-yellow-900/40 dark:text-yellow-400",
      accent: "from-yellow-400 to-amber-500",
    },
    red: {
      text: "text-red-700 dark:text-red-400",
      iconBg: "bg-red-50 text-red-600 dark:bg-red-900/40 dark:text-red-400",
      accent: "from-red-400 to-red-600",
    },
    blue: {
      text: "text-blue-700 dark:text-blue-400",
      iconBg: "bg-blue-50 text-blue-600 dark:bg-blue-900/40 dark:text-blue-400",
      accent: "from-blue-400 to-blue-600",
    },
  };
  const t = toneClass[tone];

  return (
    <div className="card group relative animate-fade-in-up overflow-hidden p-4 hover:-translate-y-0.5 hover:shadow-card-hover">
      <div className={clsx("absolute inset-x-0 top-0 h-1 bg-gradient-to-r opacity-80", t.accent)} />
      <div className="flex items-start justify-between">
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
        {icon && (
          <span className={clsx("flex h-8 w-8 items-center justify-center rounded-lg transition-transform duration-200 group-hover:scale-105", t.iconBg)}>
            {icon}
          </span>
        )}
      </div>
      <p className={clsx("mt-2 text-3xl font-bold tracking-tight", t.text)}>{value}</p>
      {hint && <p className="mt-1 text-xs text-slate-400 dark:text-slate-500">{hint}</p>}
    </div>
  );
}
